import { Box, Button, Center, Heading, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import type { IconProps } from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PaymentView from '../components/PaymentView';
import Receipt from '../components/Receipt';
import AuthContext from '../contexts/AuthContext';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import MerchantContext from '../contexts/MerchantContext';
import { SubscriptionManagementProvider } from '../contexts/SubscriptionManagementContext';
import { SubscriptionsProvider } from '../contexts/SubscriptionsContext';
import { useTRPC } from '../trpc/client';
import { INVOICE_STATUS } from '../constants/enums';
import { useQuery } from '@tanstack/react-query';

const WarningIcon = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
    />
  </Icon>
);

const PublicPaymentContainer = (): JSX.Element => {
  const trpc = useTRPC();
  const { merchantId, invoiceId } = useParams<{ merchantId: string; invoiceId: string }>();
  const [isInitialized, setIsInitialized] = useState(false);
  const { setMerchant } = useContext(MerchantContext);
  const { clearInvoicesToPay, addInvoiceToPay } = useContext(InvoicesToPayContext);
  const { setInvoiceData } = useContext(InvoiceDataContext);
  const { setDocumentNumber } = useContext(AuthContext);

  const queryOptions = trpc.invoices.getApiInvoice.queryOptions({
    rentalsMerchantId: merchantId || '',
    id: invoiceId || '',
  });

  const { data, isPending, error } = useQuery({
    ...queryOptions,
    enabled: !!merchantId && !!invoiceId,
    retry: false,
    refetchInterval: 2000,
  });

  const errorMessage = useMemo(() => {
    if (error) {
      switch (error?.message) {
        case 'Invoice not found':
          return 'La transacción que intentas pagar no existe. Por favor verifica el enlace o contacta al comercio.';
        default:
          return 'Ocurrió un error al cargar la información de pago. Por favor intenta nuevamente o contacta al comercio.';
      }
    }
    return null;
  }, [error]);

  const shouldRenderReceipt =
    !!data && (data.invoice.status !== INVOICE_STATUS.READY || !!data.invoice.paymentDate);

  useEffect(() => {
    if (!data) return;

    if (!data.invoice.cancelOnDeclined) {
      window.location.href = `/${merchantId}?factura=${invoiceId}`;
      return;
    }

    if (!isInitialized) {
      setIsInitialized(true);
      setMerchant(data.merchant);
      setInvoiceData({
        isBlockedForCardPayments: data.isBlockedForCardPayments,
        cardFeePercentage: data.cardFeePercentage,
        creditsBalance: data.creditsBalance ?? 0,
      });
      if (data.invoice.status === INVOICE_STATUS.READY && !data.invoice.paymentDate) {
        clearInvoicesToPay();
        addInvoiceToPay(data.invoice, false, 0);
      }
    }
  }, [
    data,
    isInitialized,
    setMerchant,
    clearInvoicesToPay,
    addInvoiceToPay,
    setInvoiceData,
    merchantId,
    invoiceId,
  ]);

  // Seed document number for public push checkout. Cleared on route change.
  useEffect(() => {
    if (!data?.invoice) return;
    setDocumentNumber(data.invoice.customerDocumentNumber);
    return () => {
      setDocumentNumber(undefined);
    };
  }, [data?.invoice, setDocumentNumber]);

  if (!merchantId || !invoiceId) {
    return (
      <Center h="100vh" p={6}>
        <VStack spacing={6} maxW="md" textAlign="center">
          <Box p={4} bg="orange.50" borderRadius="full">
            <WarningIcon boxSize={12} color="orange.500" />
          </Box>
          <VStack spacing={2}>
            <Heading size="lg" fontWeight="600" color="gray.800">
              Enlace de pago inválido
            </Heading>
            <Text color="gray.600" fontSize="md">
              El enlace que has seguido no contiene la información necesaria para procesar el pago.
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  if (isPending || !isInitialized) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (errorMessage || !data) {
    return (
      <Center h="100vh" p={6}>
        <VStack spacing={6} maxW="md" textAlign="center">
          <Box p={4} bg="red.50" borderRadius="full">
            <WarningIcon boxSize={12} color="red.500" />
          </Box>
          <VStack spacing={2}>
            <Heading size="lg" fontWeight="600" color="gray.800">
              No se pudo cargar la información de pago
            </Heading>
            <Text color="gray.600" fontSize="md">
              {errorMessage}
            </Text>
          </VStack>
          <Button size="lg" width="full" onClick={() => window.location.reload()}>
            REINTENTAR
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <SubscriptionsProvider
      customerDocumentNumberOverride={data.invoice.customerDocumentNumber}
      hasPendingInvoicesOverride={data.invoice.status === INVOICE_STATUS.READY}
      rentalsMerchantIdOverride={data.merchant.id}
    >
      <SubscriptionManagementProvider>
        <Box minH="100vh" display="flex" flexDirection="column">
          {shouldRenderReceipt ? (
            <Box
              p={{ base: 4, md: 8 }}
              bg="gray.50"
              minH="100vh"
              display="flex"
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="center"
            >
              <Receipt txnId={invoiceId ? [invoiceId] : undefined} />
            </Box>
          ) : (
            <PaymentView invoice={data.invoice} />
          )}
        </Box>
      </SubscriptionManagementProvider>
    </SubscriptionsProvider>
  );
};

export default PublicPaymentContainer;
